import * as React from 'react'
import { ReactElement, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, DialogContent, makeStyles, MessageBar, MessageBarActions, MessageBarBody, Text, tokens } from '@fluentui/react-components'
import * as Yup from 'yup'
import { useFormikContext } from 'formik'
import FormContainer from '../../Form/FormContainer'
import FormikInputField from '../../../../components/Formik/FormikInputField'
import FormikRichTextField from '../../../../components/Formik/FormikRichTextField'
import FormattedDateTime from '../../../../components/Date/FormattedDateTime'
import { TimeFormats } from '../../../../helpers/dateHelper'
import { NoteMutationDto } from '../../../../config/noteConfig'
import { NoteDialogBodyProps, NoteMutationDialogProps } from './NoteMutationDialogBody'

const TITLE_MAX_LENGTH = 100

const useStyles = makeStyles({
    metadata: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXXS,
        color: tokens.colorNeutralForeground3,
    },
})

export const getValidationSchema = (translate: (key: string, options?: any) => string): Yup.ObjectSchema<any> =>
    Yup.object().shape({
        fileId: Yup.string().uuid().label(translate('file.pivot.note.field.fileid')).required(),
        title: Yup.string().trim().max(TITLE_MAX_LENGTH).label(translate('file.pivot.note.field.title')).required(),
    })

export default function NoteMutationDialogBodyContent(
    props: NoteMutationDialogProps & {
        focusTrigger?: number
        confirmDelete?: boolean
        onConfirmDelete?: () => void
        onCancelDelete?: () => void
    },
): ReactElement {
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const { initialValues } = useFormikContext<NoteMutationDto>()

    const titleRef = useRef<HTMLInputElement>(null)

    const dialogBodyProps = useMemo(() => props.dialogFormBodyProps?.() ?? ({} as NoteDialogBodyProps), [props])
    const item = dialogBodyProps.item

    useEffect(() => {
        props.setValidationSchema?.(getValidationSchema(translate))
    }, [])

    useEffect(() => {
        // A new note opens empty, so put the caret in the title (AC 2); on an existing note select the title so
        // it can be replaced by typing without clearing it first.
        titleRef.current?.focus()
        if (initialValues.title) {
            titleRef.current?.setSelectionRange(0, initialValues.title.length)
        }
    }, [props.focusTrigger])

    return (
        <DialogContent>
            <FormContainer>
                {props.unsavedWarning && (
                    <MessageBar intent={'warning'} layout={'multiline'}>
                        <MessageBarBody>{translate('base.unsaved.dialog')}</MessageBarBody>
                    </MessageBar>
                )}
                {props.confirmDelete && (
                    <MessageBar intent={'error'} layout={'multiline'}>
                        <MessageBarBody>{translate('file.pivot.note.delete.confirm')}</MessageBarBody>
                        <MessageBarActions>
                            <Button appearance={'primary'} onClick={() => props.onConfirmDelete?.()}>
                                {translate('action.delete')}
                            </Button>
                            <Button onClick={() => props.onCancelDelete?.()}>{translate('action.cancel')}</Button>
                        </MessageBarActions>
                    </MessageBar>
                )}

                <FormikInputField<NoteMutationDto>
                    required
                    fieldName={'title'}
                    placeholderKey={'file.pivot.note.field.title'}
                    componentProps={{
                        // A hard stop at the maximum, so nothing is silently cut off on save; the field says so
                        // itself once the limit is reached (AC 6).
                        maxLength: TITLE_MAX_LENGTH,
                        input: { ref: titleRef },
                    }}
                />
                <FormikRichTextField<NoteMutationDto> allowLinks fieldName={'description'} placeholderKey={'file.pivot.note.field.description'} />

                {item && (
                    <div className={styles.metadata}>
                        <Text size={200}>
                            {translate('file.pivot.note.metadata.created', { author: item.createdByAuthor?.fullName })}{' '}
                            <FormattedDateTime date={item.createdAt} format={TimeFormats.ISO} />
                        </Text>
                        <Text size={200}>
                            {translate('file.pivot.note.metadata.updated', { author: item.updatedByAuthor?.fullName })}{' '}
                            <FormattedDateTime date={item.updatedAt} format={TimeFormats.ISO} />
                        </Text>
                    </div>
                )}
                {/*Source - https://stackoverflow.com/a/477699*/}
                <input type="submit" hidden />
            </FormContainer>
        </DialogContent>
    )
}
